import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismSelectInput } from '@mylighthouse/prism-ember';
import EmberPopover from '@mylighthouse/prism-ember/components/ember-popover';
import { t } from 'ember-intl';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';

import styles from './rate-compare-month.module.css';

type RateOption = { id: string; label: string };

interface HotelPricingAssistantFiltersRateCompareMonthSignature {
  Args: {
    isActive: boolean;
    onApply: (rateCode: string, referenceRateCode: string) => void;
    onRemove: () => void;
    rateCode: string | null;
    referenceRateCode: string | null;
  };
}

/* Compares the rate that becomes My rates against a reference rate in the Month layout */
export default class HotelPricingAssistantFiltersRateCompareMonth extends Component<HotelPricingAssistantFiltersRateCompareMonthSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  @tracked isPopoverVisible = false;
  @tracked selectedRate: RateOption | null = null;
  @tracked selectedReferenceRate: RateOption | null = null;

  chipTextId = `rate-compare-month-chip-text-${guidFor(this)}`;

  // Only dynamic rates for now, as the % comes from the backend's discount, which only they have
  get dynamicRateOptions(): RateOption[] {
    const dynamicRateCodes = new Set(
      this.hotelAndMarketService.hotel.primaDynamicRates.map(
        (rate) => rate.rate_code,
      ),
    );

    return this.rateOptions.filter((option) => dynamicRateCodes.has(option.id));
  }

  get isApplyDisabled(): boolean {
    return !this.selectedRate || !this.selectedReferenceRate;
  }

  get rateLabel(): string {
    return this.labelFor(this.args.rateCode);
  }

  get rateOptions(): RateOption[] {
    return this.hotelAndMarketService.hotel.primaRateCodes;
  }

  get referenceRateLabel(): string {
    return this.labelFor(this.args.referenceRateCode);
  }

  /* Only BAR for now, the rate the backend's discount is against. Comparing any rate means
     offering every rate here but the one in the first select. */
  get referenceRateOptions(): RateOption[] {
    const { primaDefaultStaticRateCode } = this.hotelAndMarketService.hotel;

    return this.rateOptions.filter(
      (option) => option.id === primaDefaultStaticRateCode,
    );
  }

  labelFor(rateCode: string | null): string {
    return (
      this.rateOptions.find((option) => option.id === rateCode)?.label ?? ''
    );
  }

  @action
  apply(): void {
    if (!this.selectedRate || !this.selectedReferenceRate) {
      return;
    }

    this.hidePopover();
    this.args.onApply(this.selectedRate.id, this.selectedReferenceRate.id);
  }

  // Re-seeded on every open, so a cancelled choice does not stick. A lone reference rate is preselected, so Apply only waits for the rate.
  @action
  handleShow(): void {
    const { referenceRateOptions } = this;

    this.selectedRate =
      this.dynamicRateOptions.find(
        (option) => option.id === this.args.rateCode,
      ) ?? null;
    this.selectedReferenceRate =
      referenceRateOptions.length === 1
        ? (referenceRateOptions[0] ?? null)
        : null;
  }

  @action
  hidePopover(): void {
    this.isPopoverVisible = false;
  }

  @action
  selectRate(option: RateOption | null): void {
    this.selectedRate = option;
  }

  @action
  selectReferenceRate(option: RateOption | null): void {
    this.selectedReferenceRate = option;
  }

  <template>
    {{#if @isActive}}
      <div
        class="{{styles.chip}} flex flex-row gap-50"
        data-test-id="rate-compare-month-chip"
      >
        <div class="ml-200 flex items-center text-nowrap">
          <span
            class="text-200--bold"
            data-test-id="rate-compare-month-chip-text"
            id={{this.chipTextId}}
          >
            {{t
              "rates.compare.compare_text.a_vs_b"
              a=this.rateLabel
              b=this.referenceRateLabel
            }}
          </span>
        </div>
        <div class="mr-50 ml-50 flex items-center justify-center">
          <PrismButton
            @buttonType="ghost"
            @icon="cross"
            @onClick={{@onRemove}}
            @size="small"
            aria-describedby={{this.chipTextId}}
            aria-label={{t "general.remove"}}
            data-test-id="rate-compare-month-remove"
          />
        </div>
      </div>
    {{else}}
      <div class="flex items-center">
        <PrismButton
          @buttonType="secondary"
          @iconBefore="compare"
          @label={{t "general.compare"}}
          data-test-id="rate-compare-month-button"
        />
        <EmberPopover
          @animationDuration={{0}}
          @event="click"
          @isShown={{this.isPopoverVisible}}
          @onShow={{this.handleShow}}
          @popoverHideDelay={{0}}
          {{! @glint-expect-error: Incorrect type }}
          @side="bottom-end"
          @spacing={{0}}
          @tooltipClass={{styles.popover}}
        >
          <div
            class={{styles.filters}}
            data-test-id="rate-compare-month-popover"
          >
            <div class={{styles.title}}>
              {{t "rates.compare.create_a_comparison"}}
            </div>
            <div class="flex flex-row items-center">
              <div
                class="{{styles.select}} flex flex-col"
                data-test-id="rate-compare-month-rate-select"
              >
                <PrismSelectInput
                  @onChange={{this.selectRate}}
                  @options={{this.dynamicRateOptions}}
                  @placeholder={{t "general.select_a_value"}}
                  @unsafe_renderInPlace={{true}}
                  @value={{this.selectedRate}}
                />
              </div>
              <div class={{styles.vs}}>{{t "revenue_insight.vs"}}</div>
              <div
                class="{{styles.select}} flex flex-col"
                data-test-id="rate-compare-month-reference-select"
              >
                <PrismSelectInput
                  @onChange={{this.selectReferenceRate}}
                  @options={{this.referenceRateOptions}}
                  @placeholder={{t "general.select_a_value"}}
                  @unsafe_renderInPlace={{true}}
                  @value={{this.selectedReferenceRate}}
                />
              </div>
            </div>
          </div>
          <div class={{styles.actions}}>
            <PrismButton
              @buttonType="secondary"
              @label={{t "general.cancel"}}
              @onClick={{this.hidePopover}}
              data-test-id="rate-compare-month-cancel"
            />
            <PrismButton
              @isDisabled={{this.isApplyDisabled}}
              @label={{t "general.apply"}}
              @onClick={{this.apply}}
              data-test-id="rate-compare-month-apply"
            />
          </div>
        </EmberPopover>
      </div>
    {{/if}}
  </template>
}
